import React, { useState, useEffect } from 'react';
import { Info } from 'lucide-react';
import api from '../../services/api';

const PRIMARY = '#2563EB';

const BasicInfoTab = ({ formData, masterData, errors, onChange, isGeneratingCode }) => {
    const jobTypes  = masterData.jobTypes  || [];
    const customers = masterData.customers || [];
    const employees = masterData.employees || [];
    const vessels   = masterData.vessels   || [];
    const airlines  = masterData.airlines  || [];
    const ports     = masterData.ports     || [];
    const countries = masterData.countries || [];

    const [isSubJob,        setIsSubJob]        = useState(false);
    const [mainJobs,        setMainJobs]        = useState([]);
    const [selectedMainJob, setSelectedMainJob] = useState(null);
    const [mainJobInfo,     setMainJobInfo]     = useState(null);
    const [loadingMainJobs, setLoadingMainJobs] = useState(false);

    useEffect(() => {
        if (formData.MainJobID && formData.MainJobID > 0) {
            setIsSubJob(true);
            setSelectedMainJob(formData.MainJobID);
        }
    }, [formData.MainJobID]);

    const loadMainJobs = async () => {
        try {
            setLoadingMainJobs(true);
            const response = await api.get('/JobGeneration.php/main-jobs');
            if (response.data.success) setMainJobs(response.data.data.mainJobs);
        } catch (err) {
            alert('Failed to load main jobs: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoadingMainJobs(false);
        }
    };

    const handleSubJobToggle = async (checked) => {
        setIsSubJob(checked);
        if (checked) {
            await loadMainJobs();
            onChange('StatusSub', true);
            onChange('MainJobID', null);
        } else {
            setSelectedMainJob(null);
            setMainJobInfo(null);
            onChange('StatusSub', false);
            onChange('MainJobID', null);
            ['ConsignerID','ConsigneeID','InvoiceToID','ShipperID','EmployeeID',
             'LoadPortID','DestinationPortID','VesselID','AirlineID',
             'TransporterID','CarrierID','CountryofOriginID'].forEach(f => onChange(f, null));
        }
    };

    const handleMainJobSelect = async (mainJobId) => {
        setSelectedMainJob(mainJobId);
        if (!mainJobId) { setMainJobInfo(null); onChange('MainJobID', null); return; }
        try {
            const response = await api.get(`/JobGeneration.php/main-job-details/${mainJobId}`);
            if (response.data.success) {
                const data = response.data.data;
                setMainJobInfo(data);
                onChange('MainJobID', parseInt(mainJobId));
                ['JobTypeID','ConsignerID','ConsigneeID','InvoiceToID','ShipperID','EmployeeID',
                 'LoadPortID','DestinationPortID','VesselID','AirlineID',
                 'TransporterID','CarrierID','CountryofOriginID'].forEach(f => onChange(f, data[f]));
            }
        } catch (err) {
            alert('Failed to load main job details: ' + (err.response?.data?.message || err.message));
        }
    };

    // shared classes
    const inputCls  = (err, readOnly) =>
        `w-full px-3 py-2.5 border rounded-md text-sm outline-none transition-all duration-150
        focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)]
        ${err    ? 'border-red-400 bg-red-50' : 'border-[#CBD5E1]'}
        ${readOnly ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`;
    const selectCls = (err, disabled) =>
        `w-full px-3 py-2.5 border rounded-md text-sm outline-none transition-all duration-150
        focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)]
        ${err     ? 'border-red-400 bg-red-50' : 'border-[#CBD5E1] bg-white'}
        ${disabled ? 'opacity-60 cursor-not-allowed bg-gray-50' : 'cursor-pointer'}`;
    const textareaCls = `w-full px-3 py-2.5 border border-[#CBD5E1] rounded-md text-sm bg-white outline-none resize-y transition-all duration-150 focus:border-[#2563EB] focus:shadow-[0_0_0_3px_rgba(37,99,235,0.12)] font-[inherit]`;
    const labelCls  = 'block mb-1.5 text-sm font-medium text-gray-700';
    const errMsg    = (msg) => msg ? <p className="text-xs text-red-500 mt-1">{msg}</p> : null;

    const SectionTitle = ({ title }) => (
        <div className="flex items-center gap-2 mb-4 pb-2 border-b-2 border-gray-200">
            <div className="w-1 h-5 rounded-full" style={{ background: PRIMARY }} />
            <h3 className="text-base font-semibold text-gray-800 m-0">{title}</h3>
        </div>
    );

    const renderSelect = (field, label, options, required = false, helpText = null, disabled = false) => (
        <div className="flex flex-col">
            <label className={labelCls}>
                {label}
                {required  && <span className="text-red-500 ml-1">*</span>}
                {helpText  && <span className="ml-1 text-gray-400 cursor-help" title={helpText}><Info size={13} className="inline" /></span>}
            </label>
            <select value={formData[field] || ''}
                onChange={e => onChange(field, e.target.value ? parseInt(e.target.value) : null)}
                disabled={disabled} className={selectCls(errors[field], disabled)}>
                <option value="">Select {label}</option>
                {options.map(opt => (
                    <option key={opt.id} value={opt.id}>
                        {opt.name}{opt.ReferenceCode ? ` (${opt.ReferenceCode})` : ''}
                    </option>
                ))}
            </select>
            {errMsg(errors[field])}
        </div>
    );

    const renderInput = (field, label, type = 'text', required = false) => (
        <div className="flex flex-col">
            <label className={labelCls}>
                {label}{required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <input type={type} value={formData[field] || ''}
                onChange={e => onChange(field, e.target.value)}
                readOnly={field === 'JobCode'}
                className={inputCls(errors[field], field === 'JobCode')} />
            {errMsg(errors[field])}
        </div>
    );

    const selectedJobType = jobTypes.find(jt => jt.id === formData.JobTypeID);
    const isSeaJob = !selectedJobType || selectedJobType.StatusSea === 1;

    return (
        <div className="space-y-8">

            {/* ── Job Information ── */}
            <section>
                <SectionTitle title="Job Information" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                    {renderSelect('JobTypeID', 'Job Type', jobTypes, true, null, isSubJob)}
                    {renderInput('JobCode', 'Job Code', 'text', true)}
                    {renderInput('JobDate', 'Job Date', 'date', true)}
                </div>

                {/* Sub-job toggle */}
                <div className="mb-4">
                    <label className="inline-flex items-center gap-2.5 px-4 py-3 bg-gray-50 border-2 border-transparent rounded-lg cursor-pointer font-medium transition-all duration-200 hover:bg-gray-100 hover:border-[#93C5FD]">
                        <input type="checkbox" checked={isSubJob}
                            onChange={e => handleSubJobToggle(e.target.checked)}
                            className="w-4 h-4 cursor-pointer accent-[#2563EB]" />
                        <span className="text-sm text-gray-700">This is a Sub-Job</span>
                        {isSubJob && (
                            <span className="ml-1 px-2 py-0.5 text-[10px] font-bold rounded uppercase" style={{ background: '#FEF3C7', color: '#B45309' }}>SUB-JOB</span>
                        )}
                    </label>
                </div>

                {/* Main job picker */}
                {isSubJob && (
                    <div className="px-5 py-4 rounded-r-lg mb-4"
                        style={{ background: '#EFF6FF', borderLeft: '4px solid #2563EB', animation: 'slideDown 0.3s ease-out' }}>
                        <div className="flex flex-col mb-3">
                            <label className={labelCls}>Select Main Job <span className="text-red-500">*</span></label>
                            <select value={selectedMainJob || ''}
                                onChange={e => handleMainJobSelect(e.target.value)}
                                disabled={loadingMainJobs}
                                className={selectCls(errors.MainJobID, loadingMainJobs)}>
                                <option value="">-- Select Main Job --</option>
                                {mainJobs.map(job => <option key={job.id} value={job.id}>{job.displayText}</option>)}
                            </select>
                            {errMsg(errors.MainJobID)}
                            {loadingMainJobs && <small className="mt-1 text-xs text-gray-500 italic">Loading main jobs...</small>}
                        </div>

                        {mainJobInfo && (
                            <div className="mt-3 p-4 bg-white rounded-lg border border-[#93C5FD]">
                                <h4 className="text-sm font-bold mb-3 mt-0" style={{ color: '#1D4ED8' }}>Main Job Information</h4>
                                {[
                                    { label: 'Job Code',  value: mainJobInfo.MainJobCode    },
                                    { label: 'Job Type',  value: mainJobInfo.JobDescription },
                                    { label: 'Customer',  value: mainJobInfo.InvoiceToName  },
                                ].map(({ label, value }) => (
                                    <div key={label} className="flex justify-between items-center py-2.5 border-b border-[#EFF6FF] last:border-b-0">
                                        <span className="text-sm font-semibold text-gray-500 min-w-[140px]">{label}:</span>
                                        <span className="text-sm text-gray-800 text-right">{value}</span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {isGeneratingCode && (
                    <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm mt-2" style={{ background: '#FFFBEB', border: '1px solid #FCD34D', color: '#B45309' }}>
                        <Info size={15} style={{ color: '#F59E0B' }} className="flex-shrink-0" /><span>Generating job code...</span>
                    </div>
                )}
            </section>

            {/* ── Customer Information ── */}
            <section>
                <SectionTitle title="Customer Information" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                    {renderSelect('InvoiceToID', 'Invoice To', customers, true,
                        'Selecting this will auto-fill Consigner and Consignee with the same customer')}
                </div>

                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-3">
                    {/* Consigner */}
                    <div className="flex flex-col">
                        <label className={labelCls}>
                            Consigner
                            {formData.ConsignerID === formData.InvoiceToID && formData.ConsignerID && (
                                <span className="ml-2 px-1.5 py-0.5 text-white text-[10px] font-bold rounded" style={{ background: '#16A34A' }} title="Auto-filled from Invoice To">✓ Auto</span>
                            )}
                        </label>
                        <select value={formData.ConsignerID || ''}
                            onChange={e => onChange('ConsignerID', e.target.value ? parseInt(e.target.value) : null)}
                            className={selectCls(false, false)}>
                            <option value="">Select Consigner</option>
                            {customers.map(opt => <option key={opt.id} value={opt.id}>{opt.name}{opt.ReferenceCode ? ` (${opt.ReferenceCode})` : ''}</option>)}
                        </select>
                    </div>
                    {/* Consignee */}
                    <div className="flex flex-col">
                        <label className={labelCls}>
                            Consignee
                            {formData.ConsigneeID === formData.InvoiceToID && formData.ConsigneeID && (
                                <span className="ml-2 px-1.5 py-0.5 text-white text-[10px] font-bold rounded" style={{ background: '#16A34A' }} title="Auto-filled from Invoice To">✓ Auto</span>
                            )}
                        </label>
                        <select value={formData.ConsigneeID || ''}
                            onChange={e => onChange('ConsigneeID', e.target.value ? parseInt(e.target.value) : null)}
                            className={selectCls(false, false)}>
                            <option value="">Select Consignee</option>
                            {customers.map(opt => <option key={opt.id} value={opt.id}>{opt.name}{opt.ReferenceCode ? ` (${opt.ReferenceCode})` : ''}</option>)}
                        </select>
                    </div>
                </div>

                {formData.InvoiceToID && !isSubJob && (
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm mb-3" style={{ background: '#EFF6FF', border: '1px solid #93C5FD', color: '#1D4ED8' }}>
                        <Info size={15} style={{ color: '#2563EB' }} className="flex-shrink-0 mt-0.5" />
                        <span>Customer auto-filled: When you select "Invoice To", both Consigner and Consignee are automatically set to the same customer. You can change them individually if needed.</span>
                    </div>
                )}
                {isSubJob && mainJobInfo && (
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg text-sm mb-3" style={{ background: '#F0FDF4', border: '1px solid #86EFAC', color: '#15803D' }}>
                        <Info size={15} style={{ color: '#16A34A' }} className="flex-shrink-0 mt-0.5" />
                        <span>Customer information has been pre-filled from main job: {mainJobInfo.MainJobCode}. You can modify these values if needed.</span>
                    </div>
                )}

                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                    {renderSelect('ShipperID', 'Shipper', customers)}
                    {renderSelect('EmployeeID', 'Employee', employees)}
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                    {renderInput('CustRef', 'Customer Reference')}
                </div>
            </section>

            {/* ── Shipping Details ── */}
            <section>
                <SectionTitle title="Shipping Details" />
                {isSeaJob ? (
                    <>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                            {renderSelect('VesselID', 'Vessel', vessels)}
                            {renderInput('VoyageNo', 'Voyage No')}
                        </div>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                            {renderInput('MBL', 'Master B/L')}
                            {renderInput('HBL', 'House B/L')}
                        </div>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                            {renderInput('BLDate', 'B/L Date', 'date')}
                            {renderInput('SailingDate', 'Sailing Date', 'date')}
                            {renderInput('ArrivalDate', 'Arrival Date', 'date')}
                        </div>
                    </>
                ) : (
                    <>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                            {renderSelect('AirlineID', 'Airline', airlines)}
                            {renderInput('Flight', 'Flight No')}
                        </div>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                            {renderInput('MAWB', 'Master AWB')}
                            {renderInput('HAWB', 'House AWB')}
                        </div>
                        <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                            {renderInput('ArrivalDate', 'Arrival Date', 'date')}
                        </div>
                    </>
                )}
            </section>

            {/* ── Port Information ── */}
            <section>
                <SectionTitle title="Port Information" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                    {renderSelect('LoadPortID', 'Load Port', ports)}
                    {renderSelect('DestinationPortID', 'Destination Port', ports)}
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                    {renderSelect('ReceiptPlaceID', 'Receipt Place', ports)}
                    {renderSelect('DeliveryPlaceID', 'Delivery Place', ports)}
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                    {renderSelect('CountryofOriginID', 'Country of Origin', countries)}
                </div>
            </section>

            {/* ── Cargo Information ── */}
            <section>
                <SectionTitle title="Cargo Information" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                    {renderInput('GrossWeight', 'Gross Weight', 'number')}
                    {renderInput('Volume', 'Volume (CBM)', 'number')}
                    {renderInput('Package', 'Package')}
                </div>
            </section>

            {/* ── References ── */}
            <section>
                <SectionTitle title="References" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                    {renderInput('IPTNo', 'IPT No')}
                    {renderInput('RefNo', 'Reference No')}
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                    {renderInput('DeliveryOrderNo', 'Delivery Order No')}
                    {renderInput('CollectionPoint', 'Collection Point')}
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                    {renderInput('MRN', 'MRN')}
                    {renderInput('RotationNo', 'Rotation No')}
                </div>
            </section>

            {/* ── Financial Information ── */}
            <section>
                <SectionTitle title="Financial Information" />
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4 mb-4">
                    <div className="flex flex-col">
                        <label className={labelCls}>Invoice Type</label>
                        <select value={formData.StausInvoiceType || 'CR'}
                            onChange={e => onChange('StausInvoiceType', e.target.value)}
                            className={selectCls(false, false)}>
                            <option value="CR">Credit</option>
                            <option value="CS">Cash</option>
                        </select>
                    </div>
                    {renderInput('Freight', 'Freight')}
                    {renderInput('BLStatus', 'B/L Status')}
                </div>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(220px,1fr))] gap-4">
                    {renderInput('CLFValue', 'CLF Value', 'number')}
                    {renderInput('DepositAmount', 'Deposit Amount', 'number')}
                    {renderInput('RefundAmount', 'Refund Amount', 'number')}
                </div>
            </section>

            {/* ── Instructions & Remarks ── */}
            <section>
                <SectionTitle title="Instructions & Remarks" />
                {[
                    { field: 'ShippingInstruction',    label: 'Shipping Instructions',    rows: 3 },
                    { field: 'DeliveryInstructions',   label: 'Delivery Instructions',    rows: 3 },
                    { field: 'CollectionInstructions', label: 'Collection Instructions',  rows: 3 },
                    { field: 'Remarks',                label: 'Remarks',                  rows: 4 },
                ].map(({ field, label, rows }) => (
                    <div key={field} className="mb-4">
                        <label className={labelCls}>{label}</label>
                        <textarea value={formData[field] || ''} rows={rows}
                            onChange={e => onChange(field, e.target.value)}
                            className={textareaCls} />
                    </div>
                ))}
            </section>

            <style>{`
                @keyframes slideDown {
                    from { opacity:0; transform:translateY(-10px); }
                    to   { opacity:1; transform:translateY(0);     }
                }
            `}</style>
        </div>
    );
};

export default BasicInfoTab;